import { db, ensureSettings } from "../storage/db";
import { SCHEMA_VERSION } from "../domain/types";
import type { AITaskType, GeneratedDocument } from "../domain/types";
import { assertAllowedTaskType, type PrivacyPayload } from "../domain/privacy";
import { now, uid } from "../domain/rules";

export async function runAiAssist(input: {
  task_type: AITaskType;
  payload: PrivacyPayload;
  sentFields: string[];
  blockedFields: string[];
  module: string;
  sourceId?: string;
  signal?: AbortSignal;
}) {
  assertAllowedTaskType(input.task_type);
  const settings = await ensureSettings();
  if (settings.credits_remaining < 1) {
    throw new Error("Keine AI-Assists mehr verfügbar.");
  }
  await db.privacyGateEvents.put({
    id: uid("privacy"),
    schema_version: SCHEMA_VERSION,
    created_at: now(),
    updated_at: now(),
    task_type: input.task_type,
    sent_fields: input.sentFields,
    blocked_fields: input.blockedFields,
    confirmed: true
  });
  const adminLogin = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("admin-login") : null;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (adminLogin) headers["x-admin-login"] = adminLogin;
  const response = await fetch("/api/ai-assist", {
    method: "POST",
    headers,
    signal: input.signal,
    body: JSON.stringify({
      task_type: input.task_type,
      privacy_payload: input.payload,
      locale: "de",
      request_id: uid("req")
    })
  });
  const data = await response.json();
  if (!response.ok || !data.ok) {
    await db.aiAssistUsage.put({
      id: uid("aiusage"),
      schema_version: SCHEMA_VERSION,
      created_at: now(),
      updated_at: now(),
      task_type: input.task_type,
      module: input.module,
      credits_used: 0,
      success: false
    });
    throw new Error(data.error ?? "AI-Assist konnte nicht ausgeführt werden.");
  }
  await db.appSettings.update(settings.id, { credits_remaining: settings.credits_remaining - 1, updated_at: now() });
  await db.aiCreditLedger.put({ id: uid("credit"), schema_version: SCHEMA_VERSION, created_at: now(), updated_at: now(), delta: -1, reason: input.task_type });
  await db.aiAssistUsage.put({
    id: uid("aiusage"),
    schema_version: SCHEMA_VERSION,
    created_at: now(),
    updated_at: now(),
    task_type: input.task_type,
    module: input.module,
    credits_used: 1,
    success: true
  });
  const doc: GeneratedDocument = {
    id: uid("doc"),
    schema_version: SCHEMA_VERSION,
    created_at: now(),
    updated_at: now(),
    type: input.task_type,
    source_id: input.sourceId,
    title: `AI-Assist: ${input.task_type}`,
    content_markdown: data.content,
    status: "draft"
  };
  await db.generatedDocuments.put(doc);
  return doc;
}
